// PerfReportsPOC.js — Documentation Fonctionnelle : POC « Centre de rapports » (module Performances).
// Aperçu interactif de l'écran réel des Rapports de performance, fidèle à sa DISPOSITION :
//   - vue LISTE : 4 KPIs (Total / PDF / Excel / Archivés), barre d'outils (recherche, filtres,
//     bascule table/cartes, Générer Rapport, Synthèse) puis tableau triable (Nom, Plan, Exécution,
//     Format, Qualification, Score, Généré par, Date, Taille, Actions) ;
//   - vue DÉTAIL : en-tête + actions (Télécharger / Archiver / Supprimer), ligne du haut
//     (Métadonnées / Qualification / Score Global en cercle), Scores par type de métrique (barres),
//     Détails de l'exécution + Agrégations par type, Violations de seuils, Tendances + Anomalies.
// Graphiques = divs « maison » (pas de lib). Actions SIMULÉES localement (démo, aucun appel réseau).
// Source de vérité : automata-studio-frontend [PRF][03]-Reports.js + automata_performances (Reports/Metrics).
import React, { useState } from 'react';
import {
  FileText, FileSpreadsheet, File, Download, Archive, Trash2, RefreshCw, Filter,
  FilePlus, FileStack, LayoutList, LayoutGrid, ArrowLeft, ArrowUp, ArrowDown, ArrowUpDown,
  CheckCircle, XCircle, AlertTriangle, AlertCircle, Minus, Info, Shield, Award, Play, Target,
  BarChart3, TrendingUp, Activity, Zap, Timer, Layers, Database, Gauge,
  ChevronRight, ArrowRight, ListChecks, ShieldCheck,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// ---- Données en dur : rapports de performance générés ------------------------
// Chaque rapport porte ses métadonnées, sa qualification, son score, et le détail complet
// (métriques, agrégations, violations de seuils, tendances, anomalies) affiché en vue détail.
const REPORTS = [
  {
    id: 'RPT-2041',
    name: 'Rapport — Plan Charge Virements ACH #2041',
    testPlanName: 'Plan Charge — Virements ACH',
    executionName: 'Exécution #2041',
    format: 'PDF',
    qualification: 'PASS',
    overallScore: 92,
    generatedBy: 'y.serhrouchni',
    generatedAt: '2026-06-14 09:32',
    fileSize: '418 KB',
    reportType: 'EXECUTION',
    archived: false,
    details: {
      duration: '08 min 12 s', totalTasks: 1200, successRate: 99.2, totalMetrics: 5, totalThresholds: 4,
      metricScores: [
        { name: 'Temps de réponse', score: 95 },
        { name: 'Débit', score: 98 },
        { name: "Taux d'erreur", score: 90 },
        { name: 'Disponibilité', score: 88 },
      ],
      aggregations: [
        { type: 'Temps de réponse', avg: 142.0, max: 318.0, min: 64.0 },
        { type: 'Débit', avg: 86.4, max: 102.0, min: 71.0 },
        { type: "Taux d'erreur", avg: 0.8, max: 2.1, min: 0.0 },
      ],
      violations: [],
      trends: [
        { metric: 'Temps de réponse', direction: 'UP', description: 'Amélioration de 6 % vs exécution précédente' },
        { metric: 'Débit', direction: 'UP', description: '+4 % vs exécution précédente' },
      ],
      anomalies: [],
    },
  },
  {
    id: 'RPT-2039',
    name: 'Rapport — Plan Stress Consultation soldes #2039',
    testPlanName: 'Plan Stress — Consultation soldes',
    executionName: 'Exécution #2039',
    format: 'EXCEL',
    qualification: 'FAIL',
    overallScore: 41,
    generatedBy: 'a.diallo',
    generatedAt: '2026-06-13 17:05',
    fileSize: '256 KB',
    reportType: 'EXECUTION',
    archived: false,
    details: {
      duration: '12 min 47 s', totalTasks: 3000, successRate: 94.1, totalMetrics: 5, totalThresholds: 4,
      metricScores: [
        { name: 'Temps de réponse', score: 38 },
        { name: 'Débit', score: 45 },
        { name: "Taux d'erreur", score: 30 },
        { name: 'Disponibilité', score: 52 },
      ],
      aggregations: [
        { type: 'Temps de réponse', avg: 268.0, max: 642.0, min: 121.0 },
        { type: 'Débit', avg: 41.2, max: 58.0, min: 22.0 },
        { type: "Taux d'erreur", avg: 5.9, max: 11.4, min: 1.2 },
      ],
      violations: [
        { metric: 'Temps de réponse moyen', actual: 268.0, threshold: 200.0, operator: '<=', severity: 'CRITICAL' },
        { metric: 'Temps de réponse p95', actual: 642.0, threshold: 500.0, operator: '<=', severity: 'CRITICAL' },
        { metric: 'Débit minimal', actual: 41.2, threshold: 50.0, operator: '>=', severity: 'WARNING' },
        { metric: "Taux d'erreur max", actual: 5.9, threshold: 2.0, operator: '<=', severity: 'CRITICAL' },
      ],
      trends: [
        { metric: 'Temps de réponse', direction: 'DOWN', description: 'Dégradation de 18 % sous charge' },
        { metric: "Taux d'erreur", direction: 'DOWN', description: '+4,1 pts vs exécution précédente' },
      ],
      anomalies: [
        { metric: "Pic de latence", description: 'Latence > 600 ms entre 17:09 et 17:12', severity: 'HIGH' },
        { metric: 'Erreurs 5xx', description: 'Rafale de 142 erreurs serveur', severity: 'MEDIUM' },
      ],
    },
  },
  {
    id: 'RPT-2036',
    name: 'Rapport — Plan Endurance API REST comptes #2036',
    testPlanName: 'Plan Endurance — API REST comptes',
    executionName: 'Exécution #2036',
    format: 'PDF',
    qualification: 'WARNING',
    overallScore: 74,
    generatedBy: 'y.serhrouchni',
    generatedAt: '2026-06-12 22:10',
    fileSize: '1.2 MB',
    reportType: 'EXECUTION',
    archived: true,
    details: {
      duration: '45 min 03 s', totalTasks: 9800, successRate: 98.6, totalMetrics: 5, totalThresholds: 4,
      metricScores: [
        { name: 'Temps de réponse', score: 78 },
        { name: 'Débit', score: 82 },
        { name: "Taux d'erreur", score: 70 },
        { name: 'Disponibilité', score: 66 },
      ],
      aggregations: [
        { type: 'Temps de réponse', avg: 188.0, max: 470.0, min: 92.0 },
        { type: 'Débit', avg: 63.7, max: 79.0, min: 48.0 },
        { type: "Taux d'erreur", avg: 1.4, max: 3.8, min: 0.2 },
      ],
      violations: [
        { metric: 'Temps de réponse p95', actual: 470.0, threshold: 500.0, operator: '<=', severity: 'WARNING' },
      ],
      trends: [
        { metric: 'Débit', direction: 'STABLE', description: 'Stable sur 45 min' },
        { metric: 'Temps de réponse', direction: 'DOWN', description: 'Légère dérive en fin de test' },
      ],
      anomalies: [
        { metric: 'Fuite mémoire suspectée', description: 'Latence croissante après 30 min', severity: 'MEDIUM' },
      ],
    },
  },
  {
    id: 'SYN-0007',
    name: 'Synthèse — Plan Charge Virements ACH',
    testPlanName: 'Plan Charge — Virements ACH',
    executionName: '3 exécutions agrégées',
    format: 'EXCEL',
    qualification: 'PASS',
    overallScore: 88,
    generatedBy: 'a.diallo',
    generatedAt: '2026-06-10 11:48',
    fileSize: '702 KB',
    reportType: 'SYNTHESIS',
    archived: false,
    details: {
      duration: '—', totalTasks: 6500, successRate: 98.4, totalMetrics: 15, totalThresholds: 12,
      metricScores: [
        { name: 'Temps de réponse', score: 90 },
        { name: 'Débit', score: 91 },
        { name: "Taux d'erreur", score: 85 },
        { name: 'Disponibilité', score: 86 },
      ],
      aggregations: [
        { type: 'Temps de réponse', avg: 165.0, max: 470.0, min: 64.0 },
        { type: 'Débit', avg: 70.4, max: 102.0, min: 22.0 },
      ],
      violations: [],
      trends: [
        { metric: 'Score global', direction: 'UP', description: 'Tendance haussière sur 3 exécutions' },
      ],
      anomalies: [],
    },
  },
];

const FORMATS = {
  PDF: { label: 'PDF', icon: FileText, color: '#EF4444' },
  EXCEL: { label: 'Excel', icon: FileSpreadsheet, color: '#10B981' },
};

const QUALIFS = {
  PASS: { label: 'Pass', color: '#10B981', icon: CheckCircle },
  FAIL: { label: 'Fail', color: '#EF4444', icon: XCircle },
  WARNING: { label: 'Warning', color: '#F59E0B', icon: AlertTriangle },
  INCONCLUSIVE: { label: 'Inconclusive', color: '#6B7280', icon: Minus },
};

// Couleur d'un score (vert ≥ 80, ambre ≥ 50, rouge sinon) — règle de l'écran réel.
const scoreColor = (s) => (s >= 80 ? '#10B981' : s >= 50 ? '#F59E0B' : '#EF4444');

// ---- Sous-composants « maison » ---------------------------------------------
const FormatBadge = ({ format }) => {
  const f = FORMATS[format] || FORMATS.PDF;
  const Ic = f.icon;
  return <Badge color={f.color}><Ic className="h-3 w-3" />{f.label}</Badge>;
};

const QualifBadge = ({ q }) => {
  const c = QUALIFS[q] || QUALIFS.INCONCLUSIVE;
  const Ic = c.icon;
  return <Badge color={c.color}><Ic className="h-3 w-3" />{c.label}</Badge>;
};

// Cercle de score « maison » (conic-gradient, pas de SVG/lib).
const ScoreCircle = ({ score, size = 100 }) => {
  const color = scoreColor(score);
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <div className="rounded-full" style={{ width: size, height: size, background: `conic-gradient(${color} ${score * 3.6}deg, rgba(156,163,175,0.25) 0deg)` }} />
      <div className="absolute rounded-full bg-white dark:bg-gray-800 flex items-center justify-center" style={{ width: size - 18, height: size - 18 }}>
        <span className="text-xl font-bold" style={{ color }}>{score}%</span>
      </div>
    </div>
  );
};

// Barre horizontale « maison » (divs).
const Bar = ({ label, value, suffix = '%', color }) => (
  <div>
    <div className="flex items-center justify-between text-xs mb-1">
      <span className="text-gray-600 dark:text-gray-300">{label}</span>
      <span className="font-semibold text-gray-800 dark:text-gray-100">{value}{suffix}</span>
    </div>
    <div className="h-3 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, value)}%`, backgroundColor: color }} />
    </div>
  </div>
);

const KpiCard = ({ icon: Ic, label, value, color }) => (
  <Card className="p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
      </div>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}1A`, color }}>
        <Ic className="h-6 w-6" />
      </div>
    </div>
  </Card>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const PerfReportsPOC = () => {
  const [reports, setReports] = useState(REPORTS);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('table');         // 'table' | 'card'
  const [showFilters, setShowFilters] = useState(false);
  const [filterFormat, setFilterFormat] = useState('ALL');
  const [filterQualif, setFilterQualif] = useState('ALL');
  const [sortField, setSortField] = useState('generatedAt'); // 'name' | 'score' | 'generatedAt'
  const [sortDir, setSortDir] = useState('desc');
  const [detailId, setDetailId] = useState(null);    // null = vue liste, sinon vue détail
  const [format, setFormat] = useState('PDF');        // format d'export en vue détail
  const [exportState, setExportState] = useState('idle'); // 'idle' | 'exporting' | 'done'
  const [toast, setToast] = useState('');

  const notify = (msg) => { setToast(msg); window.setTimeout(() => setToast(''), 2200); };

  const kpis = {
    total: reports.length,
    pdf: reports.filter((r) => r.format === 'PDF').length,
    excel: reports.filter((r) => r.format === 'EXCEL').length,
    archived: reports.filter((r) => r.archived).length,
  };

  let filtered = reports.filter((r) => {
    const okSearch = `${r.name} ${r.testPlanName} ${r.executionName}`.toLowerCase().includes(search.toLowerCase());
    const okFormat = filterFormat === 'ALL' || r.format === filterFormat;
    const okQualif = filterQualif === 'ALL' || r.qualification === filterQualif;
    return okSearch && okFormat && okQualif;
  });
  filtered = [...filtered].sort((a, b) => {
    let va, vb;
    if (sortField === 'name') { va = a.name.toLowerCase(); vb = b.name.toLowerCase(); return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va); }
    if (sortField === 'score') { va = a.overallScore; vb = b.overallScore; }
    else { va = a.generatedAt; vb = b.generatedAt; }
    return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5 text-violet-500" /> : <ArrowDown className="h-3.5 w-3.5 text-violet-500" />;
  };

  const detail = detailId ? reports.find((r) => r.id === detailId) : null;
  const openDetail = (id) => { setDetailId(id); setFormat(reports.find((r) => r.id === id)?.format || 'PDF'); setExportState('idle'); };
  const backToList = () => { setDetailId(null); setExportState('idle'); };

  const runExport = (r) => { setExportState('exporting'); window.setTimeout(() => { setExportState('done'); notify(`Rapport « ${r.name} » exporté en ${FORMATS[format].label} (simulation).`); }, 800); };
  const toggleArchive = (r) => { setReports((prev) => prev.map((x) => (x.id === r.id ? { ...x, archived: !x.archived } : x))); notify(r.archived ? 'Rapport désarchivé (simulation).' : 'Rapport archivé (simulation).'); };
  const removeReport = (r) => { setReports((prev) => prev.filter((x) => x.id !== r.id)); if (detailId === r.id) backToList(); notify('Rapport supprimé (simulation).'); };

  return (
    <div className="space-y-8">
      {/* ===== (1) Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Rapports de performance</h1>
          <Badge color="#EF4444">Module Performances</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Centre de rapports</strong> (démo, actions simulées localement).
        </p>
      </div>

      {/* ===== (2) Aperçu interactif ===== */}
      <Card className="overflow-hidden">
        {/* En-tête type app (dégradé violet) */}
        <div className="px-5 py-4 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {detail && (
              <button onClick={backToList} className="p-2 rounded-xl bg-white/15 hover:bg-white/25 transition-colors shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <FileText className="h-6 w-6 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-bold leading-tight truncate">{detail ? 'Détail du rapport' : 'Centre de rapports'}</h2>
              <p className="text-xs text-white/80 truncate">
                {detail ? `${detail.name} — ${detail.testPlanName}` : 'Générez, consultez et gérez vos rapports de performance'}
              </p>
            </div>
          </div>
          {!detail && (
            <Badge color="#FFFFFF" className="bg-white/20 border-white/30 text-white shrink-0">{filtered.length} rapport(s)</Badge>
          )}
        </div>

        {/* ----- VUE LISTE ----- */}
        {!detail && (
          <div className="p-5 space-y-5">
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard icon={FileText} label="Total rapports" value={kpis.total} color="#8B5CF6" />
              <KpiCard icon={File} label="Rapports PDF" value={kpis.pdf} color="#EF4444" />
              <KpiCard icon={FileSpreadsheet} label="Rapports Excel" value={kpis.excel} color="#10B981" />
              <KpiCard icon={Archive} label="Archivés" value={kpis.archived} color="#F59E0B" />
            </div>

            {/* Barre d'outils */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3">
              <div className="flex flex-col lg:flex-row gap-3">
                <div className="flex-1">
                  <SearchBar value={search} onChange={setSearch} placeholder="Rechercher par nom, plan de test, exécution…" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => setShowFilters((s) => !s)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${showFilters || filterFormat !== 'ALL' || filterQualif !== 'ALL' ? 'bg-violet-100 dark:bg-violet-500/20 border-violet-300 dark:border-violet-500/40 text-violet-700 dark:text-violet-300' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                    <Filter className="h-4 w-4" />Filtres
                  </button>
                  <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button onClick={() => setView('table')} className={`p-1.5 rounded-md ${view === 'table' ? 'bg-white dark:bg-gray-600 shadow text-violet-600 dark:text-violet-300' : 'text-gray-400'}`}><LayoutList className="h-4 w-4" /></button>
                    <button onClick={() => setView('card')} className={`p-1.5 rounded-md ${view === 'card' ? 'bg-white dark:bg-gray-600 shadow text-violet-600 dark:text-violet-300' : 'text-gray-400'}`}><LayoutGrid className="h-4 w-4" /></button>
                  </div>
                  <button onClick={() => { setReports(REPORTS); notify('Liste rafraîchie (simulation).'); }} className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"><RefreshCw className="h-4 w-4" /></button>
                  <Button onClick={() => notify('Ouverture de l\'assistant « Générer Rapport » (simulation).')} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white"><FilePlus className="h-4 w-4" />Générer Rapport</Button>
                  <Button onClick={() => notify('Ouverture de l\'assistant « Synthèse » (simulation).')} className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white"><FileStack className="h-4 w-4" />Synthèse</Button>
                </div>
              </div>

              {showFilters && (
                <div className="flex flex-wrap gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="min-w-[150px]">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Format</label>
                    <select value={filterFormat} onChange={(e) => setFilterFormat(e.target.value)} className="w-full px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100">
                      <option value="ALL">Tous</option><option value="PDF">PDF</option><option value="EXCEL">Excel</option>
                    </select>
                  </div>
                  <div className="min-w-[150px]">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Qualification</label>
                    <select value={filterQualif} onChange={(e) => setFilterQualif(e.target.value)} className="w-full px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100">
                      <option value="ALL">Toutes</option><option value="PASS">Pass</option><option value="WARNING">Warning</option><option value="FAIL">Fail</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button onClick={() => { setFilterFormat('ALL'); setFilterQualif('ALL'); setSearch(''); }} className="text-sm text-violet-600 dark:text-violet-400 hover:underline">Réinitialiser</button>
                  </div>
                </div>
              )}
            </div>

            {/* Liste : tableau ou cartes */}
            {filtered.length === 0 ? (
              <EmptyState icon={FileText} title="Aucun rapport trouvé" message="Générez un premier rapport de performance ou ajustez vos filtres de recherche." />
            ) : view === 'table' ? (
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="text-left font-semibold px-4 py-2.5 cursor-pointer select-none" onClick={() => toggleSort('name')}><span className="inline-flex items-center gap-1">Nom <SortIcon field="name" /></span></th>
                      <th className="text-left font-semibold px-4 py-2.5 hidden md:table-cell">Plan de test</th>
                      <th className="text-left font-semibold px-4 py-2.5 hidden lg:table-cell">Exécution</th>
                      <th className="text-left font-semibold px-4 py-2.5">Format</th>
                      <th className="text-left font-semibold px-4 py-2.5 hidden md:table-cell">Qualification</th>
                      <th className="text-left font-semibold px-4 py-2.5 cursor-pointer select-none hidden lg:table-cell" onClick={() => toggleSort('score')}><span className="inline-flex items-center gap-1">Score <SortIcon field="score" /></span></th>
                      <th className="text-left font-semibold px-4 py-2.5 hidden xl:table-cell">Généré par</th>
                      <th className="text-left font-semibold px-4 py-2.5 cursor-pointer select-none hidden xl:table-cell" onClick={() => toggleSort('generatedAt')}><span className="inline-flex items-center gap-1">Date <SortIcon field="generatedAt" /></span></th>
                      <th className="text-left font-semibold px-4 py-2.5 hidden lg:table-cell">Taille</th>
                      <th className="text-right font-semibold px-4 py-2.5">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60 text-gray-700 dark:text-gray-200">
                    {filtered.map((r) => (
                      <tr key={r.id} onClick={() => openDetail(r.id)} className="hover:bg-violet-50/50 dark:hover:bg-violet-500/10 cursor-pointer">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${FORMATS[r.format].color}1A`, color: FORMATS[r.format].color }}>
                              {r.format === 'PDF' ? <FileText className="h-4 w-4" /> : <FileSpreadsheet className="h-4 w-4" />}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 dark:text-white truncate">{r.name}</p>
                              {r.archived && <span className="text-xs text-gray-400 inline-flex items-center gap-1"><Archive className="h-3 w-3" />Archivé</span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-gray-600 dark:text-gray-400 truncate">{r.testPlanName}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-gray-600 dark:text-gray-400 truncate">{r.executionName}</td>
                        <td className="px-4 py-3"><FormatBadge format={r.format} /></td>
                        <td className="px-4 py-3 hidden md:table-cell"><QualifBadge q={r.qualification} /></td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: scoreColor(r.overallScore) }}>
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: scoreColor(r.overallScore) }} />{r.overallScore}%
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell text-gray-600 dark:text-gray-400">{r.generatedBy}</td>
                        <td className="px-4 py-3 hidden xl:table-cell text-gray-500 dark:text-gray-400">{r.generatedAt}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-gray-500 dark:text-gray-400">{r.fileSize}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => { setFormat(r.format); runExport(r); }} title="Télécharger" className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10"><Download className="h-4 w-4" /></button>
                            <button onClick={() => toggleArchive(r)} title={r.archived ? 'Désarchiver' : 'Archiver'} className="p-1.5 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10"><Archive className="h-4 w-4" /></button>
                            <button onClick={() => removeReport(r)} title="Supprimer" className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map((r) => (
                  <div key={r.id} onClick={() => openDetail(r.id)} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-violet-300 dark:hover:border-violet-500/50 cursor-pointer transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${FORMATS[r.format].color}1A`, color: FORMATS[r.format].color }}>
                          {r.format === 'PDF' ? <FileText className="h-5 w-5" /> : <FileSpreadsheet className="h-5 w-5" />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{r.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{r.generatedAt}</p>
                        </div>
                      </div>
                      {r.archived && <span className="px-2 py-0.5 rounded-md text-xs bg-gray-100 dark:bg-gray-700 text-gray-500">Archivé</span>}
                    </div>
                    <div className="flex items-center gap-2 mb-3 flex-wrap"><FormatBadge format={r.format} /><QualifBadge q={r.qualification} /></div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700/60">
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400">Plan de test</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{r.testPlanName}</p>
                      </div>
                      <ScoreCircle score={r.overallScore} size={52} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ----- VUE DÉTAIL ----- */}
        {detail && (
          <div className="p-5 space-y-5">
            {/* En-tête détail + actions */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">{detail.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Généré le {detail.generatedAt} par {detail.generatedBy}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  {['PDF', 'EXCEL'].map((f) => {
                    const Ic = FORMATS[f].icon;
                    return (
                      <button key={f} onClick={() => { setFormat(f); setExportState('idle'); }} className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium ${format === f ? 'bg-white dark:bg-gray-600 shadow text-violet-600 dark:text-violet-300' : 'text-gray-500 dark:text-gray-400'}`}>
                        <Ic className="h-3.5 w-3.5" />{FORMATS[f].label}
                      </button>
                    );
                  })}
                </div>
                <Button onClick={() => runExport(detail)} disabled={exportState === 'exporting'} className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white"><Download className="h-4 w-4" />{exportState === 'exporting' ? 'Export…' : 'Télécharger'}</Button>
                <Button variant="outline" onClick={() => toggleArchive(detail)}><Archive className="h-4 w-4" />{detail.archived ? 'Désarchiver' : 'Archiver'}</Button>
                <Button variant="danger" onClick={() => removeReport(detail)}><Trash2 className="h-4 w-4" />Supprimer</Button>
              </div>
            </div>

            {exportState === 'done' && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm">
                <CheckCircle className="h-4 w-4" />Rapport <strong>{detail.name}</strong> exporté au format <strong>{FORMATS[format].label}</strong> (simulation).
              </div>
            )}

            {/* Ligne du haut : Métadonnées / Qualification / Score */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Info className="h-3.5 w-3.5" />Métadonnées</h4>
                <div className="space-y-2.5 text-sm">
                  {[
                    ['Plan de test', detail.testPlanName],
                    ['Exécution', detail.executionName],
                    ['Type', detail.reportType === 'SYNTHESIS' ? 'Synthèse' : 'Exécution'],
                    ['Taille', detail.fileSize],
                    ['Statut', detail.archived ? 'Archivé' : 'Actif'],
                  ].map(([l, v], i) => (
                    <div key={i} className="flex justify-between gap-3">
                      <span className="text-gray-500 dark:text-gray-400">{l}</span>
                      <span className="font-medium text-gray-900 dark:text-white text-right truncate">{v}</span>
                    </div>
                  ))}
                  <div className="flex justify-between gap-3"><span className="text-gray-500 dark:text-gray-400">Format</span><FormatBadge format={detail.format} /></div>
                </div>
              </div>

              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center text-center">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Shield className="h-3.5 w-3.5" />Qualification</h4>
                <QualifBadge q={detail.qualification} />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">Résultat global de performance</p>
              </div>

              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center text-center">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Award className="h-3.5 w-3.5" />Score global</h4>
                <ScoreCircle score={detail.overallScore} size={100} />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">{detail.overallScore >= 80 ? 'Performance excellente' : detail.overallScore >= 50 ? 'Performance acceptable' : 'Performance insuffisante'}</p>
              </div>
            </div>

            {/* Scores par type de métrique (barres maison) */}
            <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><BarChart3 className="h-3.5 w-3.5" />Scores par type de métrique</h4>
              <div className="space-y-3">
                {detail.details.metricScores.map((m, i) => <Bar key={i} label={m.name} value={m.score} suffix="%" color={scoreColor(m.score)} />)}
              </div>
              <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><Info className="h-3 w-3" />Barres « maison ». L'écran réel utilise des graphiques Recharts (barres, secteurs, radial).</p>
            </div>

            {/* Détails de l'exécution + Agrégations par type */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Play className="h-3.5 w-3.5" />Détails de l'exécution</h4>
                <div className="space-y-2">
                  {[
                    [Timer, 'Durée', detail.details.duration],
                    [Layers, 'Tâches totales', detail.details.totalTasks],
                    [CheckCircle, 'Taux de réussite', `${detail.details.successRate} %`],
                    [Activity, 'Métriques collectées', detail.details.totalMetrics],
                    [Target, 'Seuils configurés', detail.details.totalThresholds],
                  ].map(([Ic, l, v], i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/40 text-sm">
                      <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400"><Ic className="h-4 w-4" />{l}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Database className="h-3.5 w-3.5" />Agrégations par type</h4>
                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {detail.details.aggregations.map((a, i) => (
                    <div key={i} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700/60">
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">{a.type}</p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div><p className="text-xs text-gray-400">Moyenne</p><p className="text-sm font-bold text-blue-600 dark:text-blue-400">{a.avg.toFixed(2)}</p></div>
                        <div><p className="text-xs text-gray-400">Max</p><p className="text-sm font-bold text-red-600 dark:text-red-400">{a.max.toFixed(2)}</p></div>
                        <div><p className="text-xs text-gray-400">Min</p><p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{a.min.toFixed(2)}</p></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Violations de seuils */}
            <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5" />Violations de seuils
                {detail.details.violations.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400">{detail.details.violations.length}</span>}
              </h4>
              {detail.details.violations.length > 0 ? (
                <div className="space-y-2">
                  {detail.details.violations.map((v, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-red-50/60 dark:bg-red-500/10 border border-red-100 dark:border-red-500/30">
                      <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-500/20 flex items-center justify-center shrink-0"><AlertCircle className="h-4 w-4 text-red-500" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{v.metric}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Valeur : <span className="font-semibold text-red-600 dark:text-red-400">{v.actual.toFixed(2)}</span> | Seuil : <span className="font-semibold">{v.operator} {v.threshold.toFixed(2)}</span></p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${v.severity === 'CRITICAL' ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400' : 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400'}`}>{v.severity}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-6 text-gray-400 text-sm"><CheckCircle className="h-4 w-4 mr-2 text-emerald-500" />Aucune violation de seuil détectée</div>
              )}
            </div>

            {/* Tendances + Anomalies */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><TrendingUp className="h-3.5 w-3.5" />Analyse de tendance</h4>
                {detail.details.trends.length > 0 ? (
                  <div className="space-y-2">
                    {detail.details.trends.map((t, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700/60">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${t.direction === 'UP' ? 'bg-emerald-100 dark:bg-emerald-500/20' : t.direction === 'DOWN' ? 'bg-red-100 dark:bg-red-500/20' : 'bg-gray-100 dark:bg-gray-700'}`}>
                          {t.direction === 'UP' ? <ArrowUp className="h-4 w-4 text-emerald-500" /> : t.direction === 'DOWN' ? <ArrowDown className="h-4 w-4 text-red-500" /> : <Minus className="h-4 w-4 text-gray-400" />}
                        </div>
                        <div className="min-w-0"><p className="text-sm font-medium text-gray-900 dark:text-white">{t.metric}</p><p className="text-xs text-gray-500 dark:text-gray-400">{t.description}</p></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-6 text-gray-400 text-sm">Aucune donnée de tendance disponible</div>
                )}
              </div>

              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Zap className="h-3.5 w-3.5" />Anomalies détectées
                  {detail.details.anomalies.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">{detail.details.anomalies.length}</span>}
                </h4>
                {detail.details.anomalies.length > 0 ? (
                  <div className="space-y-2">
                    {detail.details.anomalies.map((a, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-amber-50/60 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/30">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center shrink-0"><Zap className="h-4 w-4 text-amber-500" /></div>
                        <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-900 dark:text-white">{a.metric}</p><p className="text-xs text-gray-500 dark:text-gray-400">{a.description}</p></div>
                        <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${a.severity === 'HIGH' ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400' : 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400'}`}>{a.severity}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-6 text-gray-400 text-sm"><CheckCircle className="h-4 w-4 mr-2 text-emerald-500" />Aucune anomalie détectée</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Toast simulé */}
        {toast && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Info className="h-4 w-4 text-violet-500" />{toast}
          </div>
        )}
      </Card>

      {/* ===== (3) Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Play className="h-5 w-5 text-blue-500" />1. Parcourir & sélectionner</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Consultez les <strong>KPIs</strong> (total, PDF, Excel, archivés) en haut de l'écran.</GuideStep>
              <GuideStep n={2}>Filtrez via la <strong>recherche</strong>, les <strong>filtres</strong> (format, qualification) ou triez les colonnes.</GuideStep>
              <GuideStep n={3}>Cliquez sur une ligne pour ouvrir le <strong>détail</strong> du rapport. <button onClick={() => openDetail('RPT-2039')} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><BarChart3 className="h-5 w-5 text-blue-500" />2. Analyser le détail</h3>
            <ol className="space-y-2">
              <GuideStep n={1}><strong>Métadonnées</strong>, <strong>qualification</strong> (Pass / Warning / Fail) et <strong>score global</strong> en cercle.</GuideStep>
              <GuideStep n={2}><strong>Scores par métrique</strong>, <strong>détails d'exécution</strong> et <strong>agrégations</strong> (moy / max / min).</GuideStep>
              <GuideStep n={3}><strong>Violations de seuils</strong>, <strong>tendances</strong> et <strong>anomalies</strong> détectées. <button onClick={() => openDetail('RPT-2036')} className="text-blue-600 dark:text-blue-400 underline">Voir</button></GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Download className="h-5 w-5 text-blue-500" />3. Exporter & gérer</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Choisissez le <strong>format</strong> (PDF ou Excel) puis <strong>Télécharger</strong>. <button onClick={() => { if (detail) runExport(detail); else { openDetail('RPT-2041'); } }} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}><strong>Archivez</strong> un rapport ou <strong>supprimez</strong>-le depuis les actions.</GuideStep>
              <GuideStep n={3}>Générez un nouveau rapport d'<strong>exécution</strong> ou une <strong>synthèse</strong> de plan.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== (4) Référence ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><ListChecks className="h-5 w-5 text-blue-500" />Sections d'un rapport de performance</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            [Info, 'Métadonnées', 'Plan, exécution, format, taille, type (Exécution / Synthèse), statut'],
            [ShieldCheck, 'Qualification & score', 'Verdict global (Pass / Warning / Fail) et score de performance en cercle'],
            [BarChart3, 'Scores par métrique', 'Barres par type de métrique (vert ≥ 80, ambre ≥ 50, rouge sinon)'],
            [Play, "Détails de l'exécution", 'Durée, tâches, taux de réussite, métriques & seuils configurés'],
            [AlertTriangle, 'Violations de seuils', 'Métrique, valeur mesurée, seuil, sévérité (Critical / Warning)'],
            [TrendingUp, 'Tendances & anomalies', 'Évolution par métrique et anomalies détectées'],
          ].map(([Ic, t, d], i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
              <Ic className="h-5 w-5 text-violet-500 mt-0.5 shrink-0" />
              <div><div className="text-sm font-medium text-gray-900 dark:text-white">{t}</div><div className="text-xs text-gray-500 dark:text-gray-400">{d}</div></div>
            </div>
          ))}
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Gauge className="h-5 w-5 text-blue-500" />Métriques suivies</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Métrique', 'Unité', 'Sens favorable'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {[
                  ['Temps de réponse moyen', 'ms', 'Plus bas = mieux'],
                  ['Temps de réponse p95', 'ms', 'Plus bas = mieux'],
                  ['Débit (throughput)', 'req/s', 'Plus haut = mieux'],
                  ["Taux d'erreur", '%', 'Plus bas = mieux'],
                  ['Taux de réussite', '%', 'Plus haut = mieux'],
                ].map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 font-medium">{r[0]}</td>
                    <td className="px-4 py-2">{r[1]}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300 flex items-center gap-1"><ChevronRight className="h-3 w-3" />{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/performance/reports'],
              ['GET', '#10B981', '/api/automata/performance/reports/{id}'],
              ['POST', '#3B82F6', '/api/automata/performance/reports/execution/{executionId}'],
              ['POST', '#3B82F6', '/api/automata/performance/reports/synthesis/test-plan/{testPlanId}'],
              ['GET', '#10B981', '/api/automata/performance/reports/{id}/download'],
              ['PATCH', '#8B5CF6', '/api/automata/performance/reports/{id}/archive'],
              ['DELETE', '#EF4444', '/api/automata/performance/reports/{id}'],
              ['GET', '#10B981', '/api/automata/performance/metrics/execution/{executionId}/summary'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_performances / Reports &amp; Metrics</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default PerfReportsPOC;
